/**
 * FILE: packages/supabase/src/services/activityLog.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import type { Database } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";

export type ActivityLogRow = Database['public']['Tables']['activity_log']['Row']

/** Values stored in `activity_log.action` (filters use these exact strings). */
export const ACTIVITY_ACTION_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'enrollment.create', label: 'Enrollment' },
  { value: 'extension.create', label: 'Extension' },
  { value: 'student.update', label: 'Override / student update' },
  { value: 'DELETE_STUDENT', label: 'Delete student' },
  { value: 'RESTORE_STUDENT', label: 'Restore student' },
  { value: 'CHANGE_PHONE', label: 'Change phone' },
  { value: 'CREATE_JOB', label: 'Create job' },
  { value: 'UPDATE_JOB', label: 'Update job' },
  { value: 'CLOSE_JOB', label: 'Close job' },
  { value: 'CREATE_APPLICATION', label: 'Create application' },
  { value: 'UPDATE_APPLICATION_STATUS', label: 'Update application status' },
  { value: 'WITHDRAW_APPLICATION', label: 'Withdraw application' },
  { value: 'CREATE_SHORTLIST', label: 'Create shortlist' },
  { value: 'CREATE_STAFF', label: 'Create staff' },
  { value: 'UPDATE_STAFF_ROLE', label: 'Update staff role' },
  { value: 'DEACTIVATE_STAFF', label: 'Deactivate staff' },
  { value: 'CREATE_COMPANY', label: 'Create company' },
  { value: 'UPDATE_COMPANY', label: 'Update company' },
  { value: 'CREATE_RECRUITER', label: 'Create recruiter' },
  { value: 'UPDATE_RECRUITER', label: 'Update recruiter' },
  { value: 'UPSERT_CANDIDATE', label: 'Upsert candidate' },
  { value: 'BROADCAST_SENT', label: 'Broadcast sent' },
  { value: 'CREATE_COMMUNITY_LINK', label: 'Create community link' },
  { value: 'UPDATE_COMMUNITY_LINK', label: 'Update community link' },
  { value: 'DEACTIVATE_COMMUNITY_LINK', label: 'Deactivate community link' },
  { value: 'DELETE_COMMUNITY_LINK', label: 'Delete community link' },
]

export interface ActivityLogFilters {
  actions?: string[]
  startDate?: string
  endDate?: string
  actor?: string
  /** Matches entity_id, user_name, or action (substring). */
  search?: string
}

function sanitizeIlikeFragment(raw: string): string {
  return raw.replaceAll('%', '').replaceAll(',', ' ')
}

export async function getStudentActivity(phone: string, client: DbClient = defaultDbClient): Promise<ActivityLogRow[]> {
  const { data, error } = await client
    .from('activity_log')
    .select('*')
    .or(`entity_id.eq.${phone},entity_type.eq.student`)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data ?? []
}

/** Activity log entries for a single application (status changes, notes updates, etc.). */
export async function getApplicationActivityLog(applicationId: string, client: DbClient = defaultDbClient): Promise<ActivityLogRow[]> {
  const { data, error } = await client
    .from('activity_log')
    .select('*')
    .eq('entity_id', applicationId)
    .order('created_at', { ascending: false })
    .limit(50)
  if (error) throw error
  return data ?? []
}

export async function getActivityLog(
  filters: ActivityLogFilters = {},
  page = 0,
  pageSize = 50, client: DbClient = defaultDbClient
): Promise<{ data: ActivityLogRow[]; count: number }> {
  const safePage = Math.max(page, 0)
  const safeSize = Math.min(Math.max(pageSize, 1), 100)
  const from = safePage * safeSize
  const to = from + safeSize - 1

  let query = client
    .from('activity_log')
    .select('*', { count: 'exact', head: false })
    .order('created_at', { ascending: false, nullsFirst: false })
    .range(from, to)

  if (filters.actions?.length) {
    query = query.in('action', filters.actions)
  }
  if (filters.startDate) {
    query = query.gte('created_at', `${filters.startDate}T00:00:00`)
  }
  if (filters.endDate) {
    query = query.lte('created_at', `${filters.endDate}T23:59:59.999`)
  }
  if (filters.actor?.trim()) {
    const a = sanitizeIlikeFragment(filters.actor.trim())
    if (a) query = query.ilike('user_name', `%${a}%`)
  }
  if (filters.search?.trim()) {
    const s = sanitizeIlikeFragment(filters.search.trim())
    if (s) {
      query = query.or(`entity_id.ilike.%${s}%,user_name.ilike.%${s}%,action.ilike.%${s}%`)
    }
  }

  const { data, count, error } = await query
  if (error) throw error
  return { data: data ?? [], count: count ?? 0 }
}
